/*
 * Preview panel, stage, Theater, test play and results (W2-02). Design tokens only (src/ui/styles/tokens.css);
 * motion is zeroed by the global reduced-motion rule.
 */

/* ---------- panel ---------- */

.panel {
  container: preview / inline-size;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: var(--color-surface-1);
  color: var(--color-text);
  outline: none;
}

.panel:focus-visible {
  box-shadow: inset 0 0 0 var(--focus-ring-width) var(--color-focus-ring);
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-2);
  border-bottom: var(--border-width) solid var(--color-divider);
  min-height: var(--control-h-lg);
}

.toolbarGroup {
  display: flex;
  align-items: center;
  gap: var(--space-0);
}

.toolbarSpacer {
  flex: 1 1 auto;
}

/* "Approximate" / "Effect not previewed" (built-in extra.json effects drawn in this frame). */
.effectsBadge {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: var(--space-1);
  padding: 0 var(--space-2);
  border-radius: var(--radius-sm);
  background: var(--color-warning-muted);
  color: var(--color-text);
  font-size: var(--text-xs);
  line-height: 1.6;
  white-space: nowrap;
}

.effectsBadge:focus-visible {
  outline: 2px solid var(--color-focus-ring);
  outline-offset: 1px;
}

.effectsBadge[data-icon-only='true'] {
  padding: 0 var(--space-1);
  line-height: 1.9;
}

/* Narrow layouts: the toggles in the "More preview options" popover, one click each (UX-051). */
.morePopover {
  max-width: min(92vw, 280px);
}

.moreTitle {
  margin: 0 0 var(--space-2);
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.moreGrid {
  display: grid;
  grid-template-columns: repeat(6, max-content);
  gap: var(--space-1);
}

/* Some overlay is switched on inside the popover: a dot on the trigger (the tooltip names it). */
.moreOn {
  position: relative;
}

.moreOn::after {
  content: '';
  position: absolute;
  top: 3px;
  right: 3px;
  width: 5px;
  height: 5px;
  border-radius: var(--radius-pill);
  background: var(--color-accent);
}

.screenButtonLabel {
  max-width: 14ch;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.zoomLabel {
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  font-variant-numeric: tabular-nums;
  min-width: 4ch;
  text-align: right;
}

.autoKeyOn {
  color: var(--color-danger);
}

.stageArea {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  background: var(--color-surface-sunken);
}

.stageElsewhere {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-3);
  padding: var(--space-4);
  color: var(--color-text-muted);
  font-size: var(--text-sm);
  text-align: center;
}

.pickBanner {
  position: absolute;
  left: var(--space-2);
  top: var(--space-2);
  z-index: 2;
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-1) var(--space-3);
  border: var(--border-width) solid var(--color-warning);
  border-radius: var(--radius-md);
  background: var(--color-surface-overlay);
  font-size: var(--text-sm);
  box-shadow: var(--shadow-sm);
}

/* ---------- stage (created by previewLoop.ts) ---------- */

.stage {
  position: absolute;
  inset: 0;
  overflow: hidden;
  touch-action: none;
  user-select: none;
}

.glCanvas {
  position: absolute;
  display: block;
  background: var(--color-bg);
  box-shadow: 0 0 0 var(--border-width) var(--color-border);
}

.overlayCanvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}

.stageTooltip {
  position: absolute;
  z-index: 3;
  max-width: 34ch;
  padding: var(--space-1) var(--space-2);
  border: var(--border-width) solid var(--color-border-strong);
  border-radius: var(--radius-sm);
  background: var(--color-surface-overlay);
  color: var(--color-text);
  font-size: var(--text-xs);
  line-height: var(--leading-tight);
  pointer-events: none;
  box-shadow: var(--shadow-md);
}

/* ---------- readout ---------- */

/* One row at every panel width (UX-051): labels shorten and the name truncates instead of wrapping. */
.readout {
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: var(--space-1) var(--space-3);
  min-height: calc(var(--control-h-sm) + var(--space-1));
  padding: var(--space-0) var(--space-3);
  overflow: hidden;
  border-top: var(--border-width) solid var(--color-divider);
  font-size: var(--text-xs);
  color: var(--color-text-muted);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.readoutName {
  flex: 0 1 auto;
  min-width: 3ch;
  max-width: 40%;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--color-text);
  font-weight: var(--weight-semibold);
}

.readoutItem {
  display: inline-flex;
  flex: none;
  align-items: center;
  gap: var(--space-1);
}

.readoutKey {
  color: var(--color-text-subtle);
}

.readoutKeyShort {
  display: none;
}

.miniSlider {
  position: relative;
  display: inline-flex;
  align-items: center;
  width: 72px;
  height: calc(16px * var(--ui-scale));
  touch-action: none;
  user-select: none;
}

.miniTrack {
  position: relative;
  flex: 1;
  height: 4px;
  border-radius: var(--radius-pill);
  background: var(--color-surface-active);
}

.miniRange {
  position: absolute;
  height: 100%;
  border-radius: var(--radius-pill);
  background: var(--color-accent);
}

.miniThumb {
  display: block;
  width: calc(12px * var(--ui-scale));
  height: calc(12px * var(--ui-scale));
  border: 2px solid var(--color-accent);
  border-radius: var(--radius-pill);
  background: var(--color-surface-2);
}

.miniThumb:hover {
  background: var(--color-accent-muted);
}

.miniThumb:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
  outline-offset: var(--focus-ring-offset);
}

@container preview (max-width: 560px) {
  .readout {
    gap: var(--space-1) var(--space-2);
    padding: var(--space-0) var(--space-2);
  }

  .readoutName {
    max-width: 26%;
  }

  /* The full label stays for screen readers. */
  .readoutKeyLong {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }

  .readoutKeyShort {
    display: inline;
  }

  /* "(crosses in 0.75 s)": in the speed's tooltip instead. */
  .readoutExtra {
    display: none;
  }

  .miniSlider {
    width: 48px;
  }
}

/* ---------- banner ---------- */

.banner {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-1) var(--space-4);
  border-bottom: var(--border-width) solid var(--color-warning);
  background: var(--color-warning-muted);
  color: var(--color-text);
  font-size: var(--text-sm);
}

.bannerText {
  flex: 1 1 auto;
}

/* ---------- Theater and test play ---------- */

.fullWindow {
  position: fixed;
  inset: 0;
  z-index: var(--z-dialog);
  background: var(--color-surface-sunken);
  outline: none;
}

.fullStage {
  position: absolute;
  inset: 0;
}

/* Theater: one control bar at the bottom centre, clear of the HUD's pause button and score (top corners). */
.fullBar {
  position: absolute;
  left: 50%;
  bottom: var(--space-4);
  z-index: 2;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: var(--space-2) var(--space-3);
  max-width: calc(100% - 2 * var(--space-4));
  padding: var(--space-1) var(--space-1) var(--space-1) var(--space-3);
  border-radius: var(--radius-md);
  background: var(--color-surface-overlay);
  color: var(--color-text);
  font-size: var(--text-sm);
  box-shadow: var(--shadow-md);
  transform: translateX(-50%);
  opacity: 1;
  transition: opacity var(--duration-slow) var(--ease-standard);
}

.fullBar[data-idle='true'] {
  opacity: 0;
}

.fullBar:focus-within,
.fullBar:hover {
  opacity: 1;
}

.fullBarHint {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-1);
  color: var(--color-text-muted);
}

.fullHint {
  position: absolute;
  left: 50%;
  bottom: var(--space-5);
  z-index: 2;
  transform: translateX(-50%);
  max-width: min(90%, 72ch);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-md);
  background: var(--color-surface-overlay);
  color: var(--color-text);
  font-size: var(--text-sm);
  text-align: center;
  box-shadow: var(--shadow-md);
  pointer-events: none;
  transition: opacity var(--duration-slow) var(--ease-standard);
}

.fullHint[data-hidden='true'] {
  opacity: 0;
}

.pauseHotspot {
  position: absolute;
  left: 0;
  top: 0;
  z-index: 2;
  width: max(48px, 7%);
  height: max(48px, 11%);
  border: none;
  background: transparent;
  cursor: pointer;
}

.pauseHotspot:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
}

.scrim {
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-5);
  background: var(--color-scrim);
}

.card {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  width: min(100%, 560px);
  max-height: 100%;
  overflow: auto;
  padding: var(--space-5);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-lg);
  background: var(--color-surface-2);
  box-shadow: var(--shadow-lg);
}

.cardWide {
  width: min(100%, 760px);
}

.cardTitle {
  margin: 0;
  font-size: var(--text-xl);
  font-weight: var(--weight-semibold);
}

.cardRow {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
}

.cardLabel {
  color: var(--color-text-muted);
  font-size: var(--text-sm);
}

.srOnly {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* ---------- results ---------- */

.stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  gap: var(--space-3);
  margin: 0;
}

.stat {
  display: flex;
  flex-direction: column;
  gap: var(--space-0);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  background: var(--color-surface-3);
}

.stat dt {
  color: var(--color-text-muted);
  font-size: var(--text-xs);
}

.stat dd {
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--weight-semibold);
  font-variant-numeric: tabular-nums;
}

.statPerfect dd {
  color: var(--color-warning);
}

.statGood dd {
  color: var(--color-info);
}

.statBad dd {
  color: var(--color-severity-error);
}

.statMiss dd {
  color: var(--color-text-muted);
}

.histogram {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.histogramBars {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 96px;
  padding: 0 var(--space-1);
  border-bottom: var(--border-width) solid var(--color-border);
}

.bar {
  flex: 1 1 0;
  min-width: 6px;
  height: 100%;
  display: flex;
  align-items: flex-end;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

.bar:disabled {
  cursor: default;
}

.barFill {
  width: 100%;
  border-radius: var(--radius-xs) var(--radius-xs) 0 0;
  background: var(--color-info);
}

.bar[data-zone='perfect'] .barFill {
  background: var(--color-warning);
}

.bar[data-zone='bad'] .barFill {
  background: var(--color-severity-error);
}

.bar:focus-visible {
  outline: var(--focus-ring-width) solid var(--color-focus-ring);
}

.bar:hover:not(:disabled) .barFill {
  filter: brightness(1.2);
}

.histogramAxis {
  display: flex;
  justify-content: space-between;
  color: var(--color-text-subtle);
  font-size: var(--text-2xs);
}

.timingHint {
  display: flex;
  gap: var(--space-2);
  align-items: flex-start;
  padding: var(--space-3);
  border: var(--border-width) solid var(--color-info);
  border-radius: var(--radius-md);
  background: var(--color-info-muted);
  font-size: var(--text-sm);
}

/* ---------- Phira settings popover ---------- */

.phira {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  width: 300px;
  max-width: 90vw;
}

.phira:focus {
  outline: none;
}

.phiraNote {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--text-xs);
}

.phiraTitle {
  margin: 0;
  font-size: var(--text-md);
  font-weight: var(--weight-semibold);
}
